import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useFetcher, useNavigate, useRevalidator } from 'react-router'
import { OyChip, OyChipGroup } from '#app/components/oy/oy-chip.tsx'
import { OyField, OyTextarea } from '#app/components/oy/oy-field.tsx'
import {
	OyPhotoPicker,
	OyPhotoViewer,
	photoSrc,
	type PickedPhoto,
} from '#app/components/oy/oy-photo.tsx'
import { useOyToast } from '#app/components/oy/oy-toast.tsx'
import {
	OyTray,
	OyTrayField,
	OyTrayMenu,
	OyTrayNote,
	OyTrayRows,
	type OyTrayPrimary,
} from '#app/components/oy/oy-tray.tsx'
import {
	type DoorgevenRead,
	type DoorgevenResult,
} from '#app/routes/stal/doorgeven.tsx'
import { addDays } from '#app/utils/activity-presets.ts'
import {
	HORSE_STATUS_LABELS_NL,
	HORSE_STATUSES,
	type HorseStatus,
} from '#app/utils/roster-fields.ts'
import { type RuleChapter } from '#app/utils/stable-rules.server.ts'
import { RULE_CHAPTER_NL } from '#app/utils/stal-fields.ts'
import {
	type StalDayVM,
	type StalLessonVM,
} from '#app/utils/stal-roster.server.ts'
import { type StalShell } from '#app/utils/stal-shell.server.ts'

/**
 * Herontwerp P6 — the Stal-app trays (StalApp.dc.html "drawers"): one
 * provider in the layout holds which tray is open; any page or tray opens
 * another with `useStalTray().open(...)`. Every write posts to
 * /stal/doorgeven with an `intent`; the result's toast shows and the tray
 * closes. Chips are how you choose (never a select); the primary says
 * what happens; a missing field makes the primary say what is missing.
 */

export const DOORGEVEN = '/stal/doorgeven'

export type StalTrayState =
	| { kind: 'doorgeven' }
	| { kind: 'afwezig'; who?: string }
	| { kind: 'ruil'; dateKey?: string }
	| { kind: 'les-verzetten' }
	| { kind: 'else'; dateKey: string; day?: StalDayVM }
	| { kind: 'paard'; horse: string }
	| { kind: 'klus'; choreId?: string }
	| { kind: 'anders' }
	| { kind: 'dag'; dateKey: string; day?: StalDayVM }
	| { kind: 'les'; lesson: StalLessonVM }
	| { kind: 'regels'; chapter: RuleChapter }
	| { kind: 'regel'; chapter?: RuleChapter }
	| { kind: 'persoon'; personId: string }
	| { kind: 'foto'; lessonId?: string; horse?: string }
	| { kind: 'foto-view'; photoId: string }

type TrayApi = {
	open: (t: StalTrayState) => void
	close: () => void
	state: StalTrayState | null
}

const TrayContext = createContext<TrayApi | null>(null)

export function useStalTray(): TrayApi {
	const api = useContext(TrayContext)
	if (!api) throw new Error('useStalTray needs a StalTrayProvider')
	return api
}

/** Keep the last defined value so a tray survives its exit animation. */
function useRetained<T>(value: T | undefined | null): T | undefined {
	const ref = useRef<T | undefined>(value ?? undefined)
	if (value != null) ref.current = value
	return value ?? ref.current
}

export function StalTrayProvider({
	shell,
	children,
}: {
	shell: StalShell
	children: React.ReactNode
}) {
	const [state, setState] = useState<StalTrayState | null>(null)
	const api = useMemo<TrayApi>(
		() => ({ open: (t) => setState(t), close: () => setState(null), state }),
		[state],
	)
	const shown = useRetained(state)
	return (
		<TrayContext.Provider value={api}>
			{children}
			{shown ? (
				<TraySwitch
					key={trayKey(shown)}
					shell={shell}
					state={shown}
					open={state !== null}
					api={api}
				/>
			) : null}
		</TrayContext.Provider>
	)
}

function trayKey(s: StalTrayState): string {
	switch (s.kind) {
		case 'else':
		case 'dag':
			return `${s.kind}:${s.dateKey}`
		case 'paard':
			return `paard:${s.horse}`
		case 'les':
			return `les:${s.lesson.id}`
		case 'regels':
			return `regels:${s.chapter}`
		case 'persoon':
			return `persoon:${s.personId}`
		case 'foto-view':
			return `foto-view:${s.photoId}`
		default:
			return s.kind
	}
}

function TraySwitch({
	shell,
	state,
	open,
	api,
}: {
	shell: StalShell
	state: StalTrayState
	open: boolean
	api: TrayApi
}) {
	const props = { shell, open, onClose: api.close, go: api.open }
	switch (state.kind) {
		case 'doorgeven':
			return <DoorgevenTray {...props} />
		case 'afwezig':
			return <AfwezigTray {...props} who={state.who} />
		case 'ruil':
			return <RuilTray {...props} dateKey={state.dateKey} />
		case 'les-verzetten':
			return <LesVerzettenTray {...props} />
		case 'else':
			return <ElseTray {...props} dateKey={state.dateKey} day={state.day} />
		case 'paard':
			return <PaardTray {...props} horse={state.horse} />
		case 'klus':
			return <KlusTray {...props} choreId={state.choreId} />
		case 'anders':
			return <AndersTray {...props} />
		case 'dag':
			return <DagTray {...props} dateKey={state.dateKey} day={state.day} />
		case 'les':
			return <LesTray {...props} lesson={state.lesson} />
		case 'regels':
			return <RegelsTray {...props} chapter={state.chapter} />
		case 'regel':
			return <RegelTray {...props} chapter={state.chapter} />
		case 'persoon':
			return <PersoonTray {...props} personId={state.personId} />
		case 'foto':
			return (
				<FotoTray {...props} lessonId={state.lessonId} horse={state.horse} />
			)
		case 'foto-view':
			return <FotoViewTray {...props} photoId={state.photoId} />
	}
}

type TrayProps = {
	shell: StalShell
	open: boolean
	onClose: () => void
	go: (t: StalTrayState) => void
}

// ── the write hook ──────────────────────────────────────────────────────────

/**
 * One fetcher per tray; `post` sends an intent, the effect shows the toast
 * and closes (or keeps the tray open with the error line under the footer).
 */
function useDoorgeven(
	onDone?: (r: Extract<DoorgevenResult, { ok: true }>) => void,
) {
	const fetcher = useFetcher<DoorgevenResult>()
	const toast = useOyToast()
	const seen = useRef<DoorgevenResult | null>(null)
	const [error, setError] = useState<string | null>(null)
	const cb = useRef(onDone)
	cb.current = onDone
	useEffect(() => {
		if (
			fetcher.state !== 'idle' ||
			!fetcher.data ||
			fetcher.data === seen.current
		)
			return
		seen.current = fetcher.data
		if (fetcher.data.ok) {
			setError(null)
			toast.say(fetcher.data.toast)
			cb.current?.(fetcher.data)
		} else {
			setError(fetcher.data.error)
		}
	}, [fetcher.state, fetcher.data, toast])
	const post = useCallback(
		(fields: Record<string, string | number | undefined | null>) => {
			const body: Record<string, string> = {}
			for (const [k, v] of Object.entries(fields))
				if (v != null) body[k] = String(v)
			void fetcher.submit(body, { method: 'post', action: DOORGEVEN })
		},
		[fetcher],
	)
	return { post, busy: fetcher.state !== 'idle', error, data: fetcher.data }
}

function useRead<K extends DoorgevenRead['kind']>(kind: K) {
	const fetcher = useFetcher<DoorgevenRead>()
	const load = useCallback(
		(params: Record<string, string | undefined | null>) => {
			const q = new URLSearchParams()
			q.set(kind, '1')
			for (const [k, v] of Object.entries(params)) if (v != null) q.set(k, v)
			void fetcher.load(`${DOORGEVEN}?${q}`)
		},
		[fetcher, kind],
	)
	const value =
		fetcher.data && fetcher.data.kind === kind
			? (fetcher.data as Extract<DoorgevenRead, { kind: K }>)
			: null
	return { load, value, loading: fetcher.state !== 'idle' }
}

function Status({ error, busy }: { error: string | null; busy: boolean }) {
	if (!error && !busy) return null
	return (
		<p className={error ? 'st-status warm' : 'st-status'} role="status">
			{error ?? 'Bezig…'}
		</p>
	)
}

const SHORT_DAY = new Intl.DateTimeFormat('nl-NL', {
	timeZone: 'Europe/Amsterdam',
	weekday: 'short',
	day: 'numeric',
})
const LONG_DAY = new Intl.DateTimeFormat('nl-NL', {
	timeZone: 'Europe/Amsterdam',
	weekday: 'long',
	day: 'numeric',
	month: 'long',
})
const dayShort = (key: string) => SHORT_DAY.format(new Date(`${key}T12:00:00Z`))
const dayLong = (key: string) => LONG_DAY.format(new Date(`${key}T12:00:00Z`))
const WEEKDAYS_NL = [
	'zondag',
	'maandag',
	'dinsdag',
	'woensdag',
	'donderdag',
	'vrijdag',
	'zaterdag',
]

function members(shell: StalShell) {
	return shell.people.filter((p) => !p.isInstructor)
}
function others(shell: StalShell) {
	return members(shell).filter((p) => p.id !== shell.viewer.personId)
}
function personChip(p: { id: string; firstName: string; hue: string }) {
	return { avatar: { initial: p.firstName, hue: p.hue } }
}
function shiftTime(hhmm: string, minutes: number): string {
	const [h, m] = hhmm.split(':').map(Number)
	const total = Math.max(0, (h ?? 0) * 60 + (m ?? 0) + minutes)
	return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

// ── Iets doorgeven (menu) ───────────────────────────────────────────────────

function DoorgevenTray({ shell, open, onClose, go }: TrayProps) {
	const horse = shell.horses[0]?.name
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="✎"
			title="Iets doorgeven"
			intro="Kies wat. Oyka zet het in het rooster, meldt het in Overleg en werkt Elses bericht bij."
			footer={{
				cancel: null,
				primary: { label: 'Sluiten', tone: 'quiet', onClick: onClose },
			}}
		>
			<OyTrayField label="Wat wil je doorgeven">
				<OyTrayMenu
					items={[
						{
							icon: '🏖',
							label: 'Afwezig / vakantie',
							sub: 'Oyka zoekt vervanging voor je diensten',
							onClick: () => go({ kind: 'afwezig' }),
						},
						{
							icon: '🔁',
							label: 'Dienst ruilen',
							sub: 'met iemand, Oyka vraagt het',
							onClick: () => go({ kind: 'ruil' }),
						},
						{
							icon: '🏇',
							label: 'Les verzetten',
							sub: 'tijd of instructeur',
							onClick: () => go({ kind: 'les-verzetten' }),
						},
						{
							icon: 'E',
							label: 'Else vandaag/morgen',
							sub: 'komt wel of niet, aankomsttijd',
							onClick: () => go({ kind: 'else', dateKey: shell.todayKey }),
						},
						...(horse
							? [
									{
										icon: '🐴',
										label: 'Paard',
										sub: 'status, voer, dierenarts',
										onClick: () => go({ kind: 'paard', horse }),
									},
								]
							: []),
						{
							icon: '🛠',
							label: 'Klusje gedaan',
							sub: 'uren loggen',
							onClick: () => go({ kind: 'klus' }),
						},
						{
							icon: '✎',
							label: 'Iets anders',
							sub: 'vrije tekst naar de groep',
							onClick: () => go({ kind: 'anders' }),
						},
					]}
				/>
			</OyTrayField>
		</OyTray>
	)
}

// ── Afwezig ─────────────────────────────────────────────────────────────────

function nextWeekday(fromKey: string, weekday: number): string {
	let k = addDays(fromKey, 1)
	for (let i = 0; i < 8; i++) {
		if (new Date(`${k}T12:00:00Z`).getUTCDay() === weekday) return k
		k = addDays(k, 1)
	}
	return k
}

function AfwezigTray({
	shell,
	open,
	onClose,
	go,
	who: whoInit,
}: TrayProps & { who?: string }) {
	const [who, setWho] = useState(whoInit ?? shell.viewer.personId)
	const today = shell.todayKey
	const fromOptions = useMemo(() => {
		const sat = nextWeekday(today, 6)
		const mon = nextWeekday(today, 1)
		return [
			{ key: addDays(today, 1), label: 'morgen' },
			{ key: sat, label: dayShort(sat) },
			{ key: mon, label: dayShort(mon) },
			{ key: addDays(mon, 7), label: dayShort(addDays(mon, 7)) },
		]
	}, [today])
	const [from, setFrom] = useState(fromOptions[0]!.key)
	const [customFrom, setCustomFrom] = useState(false)
	const LEN = [
		{ id: 'dag', label: 'één dag', days: 0 },
		{ id: 'weekend', label: 'weekend', days: 1 },
		{ id: 'week', label: 'een week', days: 6 },
		{ id: 'twee', label: 'twee weken', days: 13 },
	]
	const [len, setLen] = useState('week')
	const [customTo, setCustomTo] = useState<string | null>(null)
	const to = customTo ?? addDays(from, LEN.find((l) => l.id === len)?.days ?? 6)
	const [note, setNote] = useState('')
	const gevolg = useRead('gevolg')
	useEffect(() => {
		gevolg.load({ who, from, to })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [who, from, to])
	const { post, busy, error } = useDoorgeven(() => {
		onClose()
	})
	const whoName = shell.people.find((p) => p.id === who)?.firstName ?? 'Iemand'
	const g = gevolg.value
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🏖"
			back={{ label: 'Terug', onClick: () => go({ kind: 'doorgeven' }) }}
			title="Afwezig"
			intro="Oyka kijkt welke diensten en lessen van jou erin vallen en vraagt de groep om over te nemen."
			footer={{
				primary: {
					label: busy ? 'Bezig…' : 'Doorgeven',
					onClick: () => post({ intent: 'afwezig', who, from, to, note }),
					disabled: busy || to < from,
				},
			}}
		>
			<OyTrayField label="Wie">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{members(shell).map((p) => (
							<OyChip
								key={p.id}
								selected={who === p.id}
								onSelect={() => setWho(p.id)}
								{...personChip(p)}
							>
								{p.firstName}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Vanaf">
				{(id) => (
					<>
						<OyChipGroup mode="single" labelledBy={id}>
							{fromOptions.map((o) => (
								<OyChip
									key={o.key}
									selected={!customFrom && from === o.key}
									onSelect={() => {
										setCustomFrom(false)
										setFrom(o.key)
									}}
								>
									{o.label}
								</OyChip>
							))}
							<OyChip
								selected={customFrom}
								onSelect={() => setCustomFrom(true)}
							>
								anders…
							</OyChip>
						</OyChipGroup>
						{customFrom ? (
							<OyField
								className="mt"
								type="date"
								aria-label="Vanaf welke dag"
								value={from}
								min={today}
								onChange={(e) => e.target.value && setFrom(e.target.value)}
							/>
						) : null}
					</>
				)}
			</OyTrayField>
			<OyTrayField label="Hoe lang">
				{(id) => (
					<>
						<OyChipGroup mode="single" labelledBy={id}>
							{LEN.map((l) => (
								<OyChip
									key={l.id}
									selected={customTo === null && len === l.id}
									onSelect={() => {
										setCustomTo(null)
										setLen(l.id)
									}}
								>
									{l.label}
								</OyChip>
							))}
							<OyChip
								selected={customTo !== null}
								onSelect={() => setCustomTo(addDays(from, 20))}
							>
								langer
							</OyChip>
						</OyChipGroup>
						{customTo !== null ? (
							<OyField
								className="mt"
								type="date"
								aria-label="Tot en met welke dag"
								value={customTo}
								min={from}
								onChange={(e) => e.target.value && setCustomTo(e.target.value)}
							/>
						) : null}
					</>
				)}
			</OyTrayField>
			<OyTrayField label="Gevolg">
				<OyTrayNote tone="warm">
					{g
						? g.duties.length === 0 && g.lessons === 0
							? `${whoName} heeft ${dayShort(from)} t/m ${dayShort(to)} geen dienst of les. Oyka zet het in het rooster.`
							: `${whoName} heeft in die periode ${g.duties.length} ${g.duties.length === 1 ? 'dienst' : 'diensten'}${g.lessons ? ` en ${g.lessons} ${g.lessons === 1 ? 'les' : 'lessen'}` : ''}${g.dutyLabels.length ? ` (${g.dutyLabels.join(', ')})` : ''}. Oyka zet de diensten op "wie?" en vraagt het in Overleg.`
						: 'Oyka kijkt wat erin valt…'}
				</OyTrayNote>
			</OyTrayField>
			<OyTrayField label="Notitie (mag leeg)">
				<OyField
					value={note}
					onChange={(e) => setNote(e.target.value)}
					placeholder="bijv. vakantie"
					maxLength={200}
				/>
			</OyTrayField>
			<Status error={error} busy={busy} />
		</OyTray>
	)
}

// ── Ruilen ──────────────────────────────────────────────────────────────────

function RuilTray({
	shell,
	open,
	onClose,
	dateKey: init,
}: TrayProps & { dateKey?: string }) {
	const [withId, setWithId] = useState<string | null>(null)
	const diensten = useRead('diensten')
	useEffect(() => {
		diensten.load({})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const days = diensten.value?.days ?? []
	const [dateKey, setDateKey] = useState<string | null>(init ?? null)
	const chosen = dateKey ?? days[0]?.dateKey ?? null
	const { post, busy, error } = useDoorgeven(() => onClose())
	const withName = others(shell).find((p) => p.id === withId)?.firstName
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🔁"
			title="Dienst ruilen"
			intro='Kies met wie. Oyka stuurt een berichtje en past het rooster aan zodra die persoon ja zegt. De "telt voor"-regel blijft staan.'
			footer={{
				primary: {
					label: busy
						? 'Bezig…'
						: withName && chosen
							? `Vraag ${withName}`
							: !chosen
								? 'Geen dienst om te ruilen'
								: 'Kies iemand',
					disabled: !withId || !chosen || busy,
					onClick: () =>
						withId &&
						chosen &&
						post({ intent: 'ruil', date: chosen, toId: withId }),
				},
			}}
		>
			<OyTrayField label="Welke dienst">
				{(id) =>
					diensten.loading && days.length === 0 ? (
						<p className="st-status">Even kijken…</p>
					) : days.length === 0 && !init ? (
						<p className="st-status">
							Je hebt de komende drie weken geen mestdienst op je naam.
						</p>
					) : (
						<OyChipGroup mode="single" labelledBy={id}>
							{(init && !days.some((d) => d.dateKey === init)
								? [{ dateKey: init, label: dayShort(init) }, ...days]
								: days
							).map((d) => (
								<OyChip
									key={d.dateKey}
									selected={chosen === d.dateKey}
									onSelect={() => setDateKey(d.dateKey)}
								>
									{d.label}
								</OyChip>
							))}
						</OyChipGroup>
					)
				}
			</OyTrayField>
			<OyTrayField label="Met wie">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{others(shell).map((p) => (
							<OyChip
								key={p.id}
								selected={withId === p.id}
								onSelect={() => setWithId(p.id)}
								{...personChip(p)}
							>
								{p.firstName}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<Status error={error} busy={busy} />
		</OyTray>
	)
}

// ── Les verzetten ───────────────────────────────────────────────────────────

const LESSON_TIMES = [
	'09:30',
	'10:00',
	'10:30',
	'11:00',
	'12:00',
	'14:00',
	'15:00',
]

function LesVerzettenTray({ shell, open, onClose, go }: TrayProps) {
	const lessons = shell.lessons.filter((l) => l.status !== 'vervalt')
	const [lessonId, setLessonId] = useState<string | null>(
		lessons[0]?.id ?? null,
	)
	const lesson = lessons.find((l) => l.id === lessonId) ?? null
	const [time, setTime] = useState<string | null>(null)
	const [custom, setCustom] = useState(false)
	const [instructorId, setInstructorId] = useState<string | null>(null)
	const chosenTime = time ?? lesson?.time ?? null
	const chosenInstructor = instructorId ?? lesson?.instructorId ?? null
	const instructors = shell.people.filter((p) => p.isInstructor)
	const { post, busy, error } = useDoorgeven(() => onClose())
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🏇"
			back={{ label: 'Terug', onClick: () => go({ kind: 'doorgeven' }) }}
			title="Les verzetten"
			intro="De nieuwe tijd wordt de dag ervoor om 18:00 per sms bevestigd; daarna pas naar Else."
			footer={{
				primary: {
					label: busy
						? 'Bezig…'
						: !lesson
							? 'Geen les om te verzetten'
							: chosenTime
								? `Verzetten naar ${chosenTime}`
								: 'Kies een tijd',
					disabled: !lesson || !chosenTime || busy,
					onClick: () =>
						lesson &&
						chosenTime &&
						post({
							intent: 'les-update',
							lessonId: lesson.id,
							startTime: chosenTime,
							instructorId: chosenInstructor ?? undefined,
						}),
				},
			}}
		>
			<OyTrayField label="Welke les">
				{(id) =>
					lessons.length === 0 ? (
						<p className="st-status">
							De komende twee weken staat er geen les.
						</p>
					) : (
						<OyChipGroup mode="single" labelledBy={id} className="wrap">
							{lessons.map((l) => (
								<OyChip
									key={l.id}
									selected={lessonId === l.id}
									onSelect={() => {
										setLessonId(l.id)
										setTime(null)
										setInstructorId(null)
									}}
								>
									{dayShort(l.dateKey)}, {l.time ?? 'tijd volgt'}, {l.riderName}
								</OyChip>
							))}
						</OyChipGroup>
					)
				}
			</OyTrayField>
			<OyTrayField label="Nieuwe tijd">
				{(id) => (
					<>
						<OyChipGroup mode="single" labelledBy={id}>
							{LESSON_TIMES.map((t) => (
								<OyChip
									key={t}
									selected={!custom && chosenTime === t}
									onSelect={() => {
										setCustom(false)
										setTime(t)
									}}
								>
									{t}
								</OyChip>
							))}
							<OyChip selected={custom} onSelect={() => setCustom(true)}>
								anders…
							</OyChip>
						</OyChipGroup>
						{custom ? (
							<OyField
								className="mt"
								type="time"
								aria-label="Eigen tijd"
								value={chosenTime ?? ''}
								onChange={(e) => e.target.value && setTime(e.target.value)}
							/>
						) : null}
					</>
				)}
			</OyTrayField>
			<OyTrayField label="Instructeur">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{instructors.map((p) => (
							<OyChip
								key={p.id}
								selected={chosenInstructor === p.id}
								onSelect={() => setInstructorId(p.id)}
								{...personChip(p)}
							>
								{p.firstName}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<Status error={error} busy={busy} />
		</OyTray>
	)
}

// ── Else vandaag / morgen ───────────────────────────────────────────────────

function ElseTray({
	shell,
	open,
	onClose,
	dateKey,
	day: given,
}: TrayProps & { dateKey: string; day?: StalDayVM }) {
	const read = useRead('dag')
	useEffect(() => {
		if (!given) read.load({ date: dateKey })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateKey])
	const day = given ?? read.value?.day ?? null
	const arrivalBase = useMemo(
		() => read.value?.arrivalOptions ?? [],
		[read.value],
	)
	const [comes, setComes] = useState<boolean | null>(null)
	const [arrival, setArrival] = useState<string | null>(null)
	const [onSite, setOnSite] = useState<string[] | null>(null)
	const [frame, setFrame] = useState<'horse' | 'rider' | null>(null)
	const eName = shell.e.firstName
	const h1 = shell.horses.find((h) => h.slot === 'h1') ?? shell.horses[0]
	const comesNow = comes ?? day?.eExpected ?? false
	const arrivalNow = arrival ?? day?.eArrival ?? null
	const onSiteNow = useMemo(() => onSite ?? day?.onSite ?? [], [onSite, day])
	const lessonTime = day?.lesson?.time ?? null
	const arrivalOptions = useMemo(() => {
		const set = new Set<string>(arrivalBase)
		if (lessonTime) {
			set.add(shiftTime(lessonTime, -75))
			set.add(shiftTime(lessonTime, -60))
			set.add(shiftTime(lessonTime, -45))
		}
		if (arrivalNow) set.add(arrivalNow)
		return [...set].sort()
	}, [arrivalBase, lessonTime, arrivalNow])
	const pending = useMemo(() => {
		const p: Record<string, string> = {
			date: dateKey,
			eExpected: String(comesNow),
		}
		if (comesNow) {
			if (arrivalNow) p.eArrival = arrivalNow
			p.onSite = onSiteNow.join(',')
		} else if (frame === 'rider') {
			p.h1Rider = shell.viewer.personId
		}
		return p
	}, [dateKey, comesNow, arrivalNow, onSiteNow, frame, shell.viewer.personId])
	const preview = useRead('preview')
	const horseWrite = useDoorgeven()
	useEffect(() => {
		if (!day) return
		preview.load(pending)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pending, day, horseWrite.data])
	const { post, busy, error } = useDoorgeven(() => onClose())
	const save = () =>
		post({
			intent: 'else-save',
			date: dateKey,
			eExpected: String(comesNow),
			...(comesNow
				? { eArrival: arrivalNow ?? '', onSite: onSiteNow.join(',') }
				: {}),
			...(!comesNow && frame === 'rider'
				? { h1Rider: shell.viewer.personId }
				: {}),
		})
	const isToday = dateKey === shell.todayKey
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="E"
			title={isToday ? `${eName} vandaag` : `${eName} ${dayShort(dateKey)}`}
			intro="Wat je hier kiest bepaalt haar ochtendbericht en het keukenscherm. Oyka formuleert het altijd positief."
			footer={{
				primary: {
					label: busy ? 'Bezig…' : 'Opslaan',
					onClick: save,
					disabled: busy || !day,
				},
			}}
		>
			{!day ? (
				<p className="st-status">Even kijken…</p>
			) : (
				<>
					<OyTrayField label={eName}>
						{(id) => (
							<OyChipGroup mode="single" labelledBy={id}>
								<OyChip
									tone="green"
									selected={comesNow}
									onSelect={() => setComes(true)}
								>
									komt
								</OyChip>
								<OyChip selected={!comesNow} onSelect={() => setComes(false)}>
									komt niet
								</OyChip>
							</OyChipGroup>
						)}
					</OyTrayField>
					{comesNow ? (
						<>
							<OyTrayField
								label={
									lessonTime
										? `Aankomst (45–75 min voor de les van ${lessonTime})`
										: 'Aankomst'
								}
							>
								{(id) => (
									<OyChipGroup mode="single" labelledBy={id}>
										{arrivalOptions.map((t) => (
											<OyChip
												key={t}
												selected={arrivalNow === t}
												onSelect={() => setArrival(t)}
											>
												{t}
											</OyChip>
										))}
									</OyChipGroup>
								)}
							</OyTrayField>
							<OyTrayField label="Wie is erbij">
								{(id) => (
									<OyChipGroup mode="multi" labelledBy={id}>
										{members(shell).map((p) => (
											<OyChip
												key={p.id}
												selected={onSiteNow.includes(p.id)}
												onSelect={() =>
													setOnSite(
														onSiteNow.includes(p.id)
															? onSiteNow.filter((x) => x !== p.id)
															: [...onSiteNow, p.id],
													)
												}
												{...personChip(p)}
											>
												{p.firstName}
											</OyChip>
										))}
									</OyChipGroup>
								)}
							</OyTrayField>
						</>
					) : (
						<OyTrayField
							label="Waarom (voor het bericht)"
							hint="Oyka schrijft een dag zonder rijden alleen met een positieve reden."
						>
							{(id) => (
								<OyChipGroup mode="single" labelledBy={id}>
									{h1 ? (
										<OyChip
											selected={
												frame === 'horse' ||
												(frame === null && h1.status === 'dagje_vrij')
											}
											onSelect={() => {
												setFrame('horse')
												horseWrite.post({
													intent: 'paard-status',
													horse: h1.name,
													status: 'dagje_vrij',
													date: dateKey,
												})
											}}
										>
											{h1.name} heeft vrij
										</OyChip>
									) : null}
									{h1 ? (
										<OyChip
											selected={frame === 'rider'}
											onSelect={() => setFrame('rider')}
										>
											{shell.viewer.firstName} rijdt {h1.name}
										</OyChip>
									) : null}
								</OyChipGroup>
							)}
						</OyTrayField>
					)}
					<OyTrayField label="Bericht">
						<OyTrayNote tone="muted">
							<span className="st-preview">
								{preview.value
									? preview.value.ok
										? preview.value.text
										: `Hier kan Oyka nog geen bericht van maken: ${preview.value.reason}.`
									: 'Oyka schrijft mee…'}
							</span>
						</OyTrayNote>
					</OyTrayField>
					<Status error={error ?? horseWrite.error} busy={busy} />
				</>
			)}
		</OyTray>
	)
}

// ── Paard ───────────────────────────────────────────────────────────────────

function PaardTray({
	shell,
	open,
	onClose,
	horse: name,
}: TrayProps & { horse: string }) {
	const horse = shell.horses.find((h) => h.name === name)
	const [editing, setEditing] = useState(false)
	const [drafts, setDrafts] = useState<Record<string, string>>({})
	const [newRow, setNewRow] = useState('')
	const status = useDoorgeven()
	const voer = useDoorgeven(() => {
		setDrafts({})
		setNewRow('')
	})
	const rider = shell.lessons.find(
		(l) => l.dateKey === shell.todayKey && l.horse === name,
	)
	const canEditFeed = shell.viewer.isAdmin
	if (!horse) return null
	const feedLabel = horse.feedLastBy
		? `Voer, laatst gewijzigd ${horse.feedLastAt} door ${horse.feedLastBy}`
		: 'Voer'
	const primary: OyTrayPrimary = editing
		? { label: 'Klaar', tone: 'quiet', onClick: () => setEditing(false) }
		: canEditFeed
			? {
					label: 'Voer aanpassen',
					tone: 'quiet',
					onClick: () => setEditing(true),
				}
			: {
					label: `Voer: beheer via ${shell.admins.join(', ') || 'beheer'}`,
					disabled: true,
				}
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🐴"
			back={
				editing
					? { label: 'Terug', onClick: () => setEditing(false) }
					: undefined
			}
			title={editing ? `Voer van ${horse.name}` : horse.name}
			intro={
				editing
					? 'Elke regel is een afspraak; een wijziging krijgt een datum en jouw naam.'
					: `${rider ? `Vandaag les ${rider.time ?? ''} met ${rider.riderName}. ` : ''}Status: ${horse.statusLabel}.`
			}
			footer={{ cancel: editing ? null : 'Sluiten', primary }}
		>
			{editing ? (
				<>
					<OyTrayField label="Voer-regels">
						<div className="oy-tray-rows">
							{horse.feed.map((r) => {
								const val = drafts[r.id] ?? r.textNl
								return (
									<div key={r.id} style={{ display: 'grid', gap: 6 }}>
										<OyField
											value={val}
											aria-label={`Regel: ${r.textNl}`}
											onChange={(e) =>
												setDrafts({ ...drafts, [r.id]: e.target.value })
											}
											maxLength={280}
										/>
										{val.trim() !== r.textNl ? (
											<button
												type="button"
												className="oy-btn oy-btn-secondary size-sm"
												onClick={() =>
													voer.post({
														intent: 'voer-edit',
														ruleId: r.id,
														text: val,
													})
												}
											>
												Bewaar wijziging
											</button>
										) : null}
									</div>
								)
							})}
						</div>
					</OyTrayField>
					<OyTrayField label="Nieuwe regel">
						<OyField
							value={newRow}
							onChange={(e) => setNewRow(e.target.value)}
							placeholder="bijv. avond: ½ schep + 1 pil"
							maxLength={280}
						/>
						<button
							type="button"
							className={
								newRow.trim()
									? 'oy-btn oy-btn-secondary size-sm'
									: 'oy-btn oy-btn-hint size-sm'
							}
							style={{ marginTop: 8 }}
							aria-disabled={!newRow.trim()}
							onClick={() =>
								newRow.trim() &&
								voer.post({
									intent: 'voer-add',
									horse: horse.name,
									text: newRow,
								})
							}
						>
							{newRow.trim() ? '+ Regel toevoegen' : 'Schrijf eerst de regel'}
						</button>
					</OyTrayField>
					<Status error={voer.error} busy={voer.busy} />
				</>
			) : (
				<>
					<OyTrayField label="Hoe gaat het">
						{(id) => (
							<OyChipGroup mode="single" labelledBy={id}>
								{HORSE_STATUSES.map((s) => (
									<OyChip
										key={s}
										tone={s === 'rijklaar' ? 'green' : 'accent'}
										selected={horse.status === s}
										onSelect={() =>
											horse.status !== s &&
											status.post({
												intent: 'paard-status',
												horse: horse.name,
												status: s,
												date: shell.todayKey,
											})
										}
									>
										{HORSE_STATUS_LABELS_NL[s as HorseStatus]}
									</OyChip>
								))}
							</OyChipGroup>
						)}
					</OyTrayField>
					<OyTrayField label={feedLabel}>
						{horse.feed.length ? (
							<OyTrayRows
								rows={horse.feed.map((r, i) => ({
									a: `${i + 1}.`,
									b: r.textNl,
								}))}
							/>
						) : (
							<p className="st-status">
								Nog geen voer-regels voor {horse.name}.
							</p>
						)}
					</OyTrayField>
					{horse.note ? (
						<OyTrayField label="Let op">
							<OyTrayRows rows={[{ a: horse.statusLabel, b: horse.note }]} />
						</OyTrayField>
					) : null}
					<Status error={status.error} busy={status.busy} />
				</>
			)}
		</OyTray>
	)
}

// ── Klusje loggen ───────────────────────────────────────────────────────────

const HOURS = [
	{ v: 0.5, label: '½ u' },
	{ v: 1, label: '1 u' },
	{ v: 2, label: '2 u' },
	{ v: 3, label: '3 u' },
]

function KlusTray({
	shell,
	open,
	onClose,
	choreId: init,
}: TrayProps & { choreId?: string }) {
	const [choreId, setChoreId] = useState<string | 'anders' | null>(
		init ?? shell.chores[0]?.id ?? 'anders',
	)
	const [text, setText] = useState('')
	const [hours, setHours] = useState<number | null>(1)
	const [more, setMore] = useState(false)
	const { post, busy, error } = useDoorgeven(() => onClose())
	const ready =
		(choreId !== 'anders' || text.trim()) && hours != null && hours >= 0
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🛠"
			title="Klusje loggen"
			intro="Wat heb je extra gedaan? Uren tellen mee in het overzicht."
			footer={{
				primary: {
					label: busy
						? 'Bezig…'
						: ready
							? 'Loggen'
							: choreId === 'anders' && !text.trim()
								? 'Schrijf eerst wat je deed'
								: 'Kies hoe lang',
					disabled: !ready || busy,
					onClick: () =>
						ready &&
						post({
							intent: 'klus-log',
							choreId: choreId === 'anders' ? undefined : choreId,
							text: choreId === 'anders' ? text : undefined,
							hours: hours ?? 0,
						}),
				},
			}}
		>
			<OyTrayField label="Wat">
				{(id) => (
					<>
						<OyChipGroup mode="single" labelledBy={id} className="wrap">
							{shell.chores.map((c) => (
								<OyChip
									key={c.id}
									selected={choreId === c.id}
									onSelect={() => setChoreId(c.id)}
								>
									{c.titleNl}
								</OyChip>
							))}
							<OyChip
								selected={choreId === 'anders'}
								onSelect={() => setChoreId('anders')}
							>
								Anders
							</OyChip>
						</OyChipGroup>
						{choreId === 'anders' ? (
							<OyField
								className="mt"
								value={text}
								onChange={(e) => setText(e.target.value)}
								placeholder="bijv. hooibalen binnengehaald"
								maxLength={200}
								aria-label="Wat heb je gedaan"
							/>
						) : null}
					</>
				)}
			</OyTrayField>
			<OyTrayField label="Hoe lang">
				{(id) => (
					<>
						<OyChipGroup mode="single" labelledBy={id}>
							{HOURS.map((h) => (
								<OyChip
									key={h.v}
									selected={!more && hours === h.v}
									onSelect={() => {
										setMore(false)
										setHours(h.v)
									}}
								>
									{h.label}
								</OyChip>
							))}
							<OyChip
								selected={more}
								onSelect={() => {
									setMore(true)
									setHours(4)
								}}
							>
								meer
							</OyChip>
						</OyChipGroup>
						{more ? (
							<OyField
								className="mt"
								type="number"
								inputMode="decimal"
								step="0.5"
								min="0"
								max="24"
								aria-label="Aantal uren"
								value={hours ?? ''}
								onChange={(e) =>
									setHours(
										e.target.value === '' ? null : Number(e.target.value),
									)
								}
							/>
						) : null}
					</>
				)}
			</OyTrayField>
			<Status error={error} busy={busy} />
		</OyTray>
	)
}

// ── Iets anders ─────────────────────────────────────────────────────────────

function AndersTray({ open, onClose, go }: TrayProps) {
	const [text, setText] = useState('')
	const navigate = useNavigate()
	const { post, busy, error } = useDoorgeven(() => {
		onClose()
		void navigate('/stal/overleg')
	})
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="✎"
			back={{ label: 'Terug', onClick: () => go({ kind: 'doorgeven' }) }}
			title="Iets anders"
			intro="Gaat als bericht naar Overleg."
			footer={{
				primary: {
					label: busy ? 'Bezig…' : text.trim() ? 'Versturen' : 'Schrijf eerst',
					disabled: !text.trim() || busy,
					onClick: () => text.trim() && post({ intent: 'anders', text }),
				},
			}}
		>
			<OyTrayField label="Bericht">
				<OyTextarea
					value={text}
					onChange={(e) => setText(e.target.value)}
					placeholder="bijv. hek bij de kleine wei hangt scheef"
					rows={3}
					maxLength={2000}
				/>
			</OyTrayField>
			<Status error={error} busy={busy} />
		</OyTray>
	)
}

// ── Dag ─────────────────────────────────────────────────────────────────────

function DagTray({
	shell,
	open,
	onClose,
	go,
	dateKey,
	day: given,
}: TrayProps & { dateKey: string; day?: StalDayVM }) {
	const read = useRead('dag')
	const revalidator = useRevalidator()
	useEffect(() => {
		if (!given) read.load({ date: dateKey })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateKey])
	const write = useDoorgeven(() => {
		read.load({ date: dateKey })
		void revalidator.revalidate()
	})
	const day = read.value?.day ?? given ?? null
	const me = shell.viewer.personId
	const dutyPeople = [...members(shell), shell.e]
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="📅"
			title={dayLong(dateKey)}
			intro={
				!day
					? 'Even kijken…'
					: day.dutyName
						? `${day.dutyName} heeft mestdienst.`
						: 'Nog niemand voor de mestdienst.'
			}
			footer={{
				cancel: null,
				primary: { label: 'Klaar', tone: 'quiet', onClick: onClose },
			}}
		>
			{day ? (
				<>
					<OyTrayField
						label="Mestdienst"
						hint="Jezelf kiezen zet het meteen in het rooster; iemand anders krijgt de vraag in Overleg."
					>
						{(id) => (
							<OyChipGroup mode="single" labelledBy={id}>
								{dutyPeople.map((p) => (
									<OyChip
										key={p.id}
										selected={day.dutyId === p.id}
										onSelect={() =>
											day.dutyId !== p.id &&
											write.post(
												p.id === me
													? { intent: 'take-duty', date: dateKey }
													: { intent: 'ask-duty', date: dateKey, toId: p.id },
											)
										}
										{...personChip(p)}
									>
										{p.firstName}
									</OyChip>
								))}
							</OyChipGroup>
						)}
					</OyTrayField>
					<OyTrayField label={shell.e.firstName}>
						{(id) => (
							<OyChipGroup mode="single" labelledBy={id}>
								<OyChip
									tone="green"
									selected={day.eExpected}
									onSelect={() =>
										!day.eExpected &&
										write.post({
											intent: 'else-save',
											date: dateKey,
											eExpected: 'true',
										})
									}
								>
									komt{day.eArrival && day.eExpected ? ` ${day.eArrival}` : ''}
								</OyChip>
								<OyChip
									selected={!day.eExpected}
									onSelect={() =>
										day.eExpected &&
										write.post({
											intent: 'else-save',
											date: dateKey,
											eExpected: 'false',
										})
									}
								>
									komt niet
								</OyChip>
								<OyChip
									selected={false}
									onSelect={() => go({ kind: 'else', dateKey, day })}
								>
									aanpassen…
								</OyChip>
							</OyChipGroup>
						)}
					</OyTrayField>
					{day.lesson ? (
						<OyTrayField label="Les">
							<OyTrayRows
								rows={[
									{
										a: day.lesson.time ?? 'tijd volgt',
										b: `${day.lesson.horse} met ${day.lesson.instructorName}, ${day.lesson.stateLabel}`,
									},
								]}
							/>
						</OyTrayField>
					) : null}
					<OyTrayField label="Wie is er">
						{(id) => (
							<OyChipGroup mode="multi" labelledBy={id}>
								{members(shell).map((p) => (
									<OyChip
										key={p.id}
										selected={day.onSite.includes(p.id)}
										onSelect={() =>
											write.post({
												intent: 'else-save',
												date: dateKey,
												onSite: (day.onSite.includes(p.id)
													? day.onSite.filter((x) => x !== p.id)
													: [...day.onSite, p.id]
												).join(','),
											})
										}
										{...personChip(p)}
									>
										{p.firstName}
									</OyChip>
								))}
							</OyChipGroup>
						)}
					</OyTrayField>
					{day.absentNames.length ? (
						<p className="st-status">Afwezig: {day.absentNames.join(', ')}</p>
					) : null}
					<Status error={write.error} busy={write.busy} />
				</>
			) : null}
		</OyTray>
	)
}

// ── Les ─────────────────────────────────────────────────────────────────────

function LesTray({
	shell,
	open,
	onClose,
	lesson,
}: TrayProps & { lesson: StalLessonVM }) {
	const [time, setTime] = useState<string | null>(null)
	const [custom, setCustom] = useState(false)
	const [instructorId, setInstructorId] = useState<string | null>(null)
	const [horse, setHorse] = useState<string | null>(null)
	const chosenTime = time ?? lesson.time
	const chosenInstructor = instructorId ?? lesson.instructorId
	const chosenHorse = horse ?? lesson.horse
	const dirty =
		chosenTime !== lesson.time ||
		chosenInstructor !== lesson.instructorId ||
		chosenHorse !== lesson.horse
	const awaiting =
		lesson.status === 'check_verstuurd' ||
		lesson.status === 'check_gepland' ||
		lesson.status === 'verzet'
	const { post, busy, error } = useDoorgeven(() => onClose())
	const instructors = shell.people.filter((p) => p.isInstructor)
	const eName = shell.e.firstName
	const arrival = chosenTime ? shiftTime(chosenTime, -60) : null
	const intro =
		lesson.status === 'bevestigd'
			? `${lesson.horse} met ${lesson.instructorName}. Bevestigd per sms.`
			: lesson.status === 'check_verstuurd'
				? `${lesson.horse} met ${lesson.instructorName}. De sms-check is verstuurd en Oyka wacht op antwoord.`
				: lesson.status === 'check_gepland'
					? `${lesson.horse} met ${lesson.instructorName}. Oyka checkt vanavond om 18:00 per sms met ${lesson.instructorName}.`
					: lesson.status === 'verzet'
						? `${lesson.horse} met ${lesson.instructorName}. ${lesson.instructorName} stelt ${lesson.proposedTime ?? 'een andere tijd'} voor.`
						: `${lesson.horse} met ${lesson.instructorName}. Wordt de dag ervoor om 18:00 gecheckt.`
	const primary: OyTrayPrimary =
		dirty && chosenTime
			? {
					label: busy ? 'Bezig…' : 'Opslaan',
					disabled: busy,
					onClick: () =>
						post({
							intent: 'les-update',
							lessonId: lesson.id,
							startTime: chosenTime,
							instructorId: chosenInstructor,
							horse: chosenHorse,
						}),
				}
			: awaiting && chosenTime
				? {
						label: busy ? 'Bezig…' : 'Nu al bevestigen',
						disabled: busy,
						onClick: () => post({ intent: 'les-confirm', lessonId: lesson.id }),
					}
				: { label: 'Sluiten', tone: 'quiet', onClick: onClose }
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🏇"
			title={`Les ${dayLong(lesson.dateKey)}`}
			intro={intro}
			footer={{ primary }}
		>
			<OyTrayField label="Tijd">
				{(id) => (
					<>
						<OyChipGroup mode="single" labelledBy={id}>
							{[
								...new Set([
									...LESSON_TIMES,
									...(lesson.time ? [lesson.time] : []),
								]),
							]
								.sort()
								.map((t) => (
									<OyChip
										key={t}
										selected={!custom && chosenTime === t}
										onSelect={() => {
											setCustom(false)
											setTime(t)
										}}
									>
										{t}
									</OyChip>
								))}
							<OyChip selected={custom} onSelect={() => setCustom(true)}>
								anders…
							</OyChip>
						</OyChipGroup>
						{custom ? (
							<OyField
								className="mt"
								type="time"
								aria-label="Eigen tijd"
								value={chosenTime ?? ''}
								onChange={(e) => e.target.value && setTime(e.target.value)}
							/>
						) : null}
					</>
				)}
			</OyTrayField>
			<OyTrayField label="Instructeur">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{instructors.map((p) => (
							<OyChip
								key={p.id}
								selected={chosenInstructor === p.id}
								onSelect={() => setInstructorId(p.id)}
								{...personChip(p)}
							>
								{p.firstName}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Paard">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{shell.horses.map((h) => (
							<OyChip
								key={h.name}
								selected={chosenHorse === h.name}
								onSelect={() => setHorse(h.name)}
							>
								{h.name}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			{lesson.riderId === 'E' ? (
				<OyTrayField label={`Voor ${eName} betekent dit`}>
					<OyTrayNote>
						{arrival
							? `Aankomst rond ${arrival} (45–75 min voor de les). De tijd komt pas op haar scherm en in haar ochtendbericht na bevestiging.`
							: 'Zonder tijd staat de les nog niet op haar scherm.'}
					</OyTrayNote>
				</OyTrayField>
			) : null}
			<Status error={error} busy={busy} />
		</OyTray>
	)
}

// ── Afspraken ───────────────────────────────────────────────────────────────

function RegelsTray({
	shell,
	open,
	onClose,
	go,
	chapter,
}: TrayProps & { chapter: RuleChapter }) {
	const rows = shell.rules[chapter] ?? []
	const meta = RULE_CHAPTER_NL[chapter]
	const canEdit = shell.viewer.isAdmin
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon={meta.icon}
			title={meta.label}
			intro={
				canEdit
					? 'Jij kunt deze afspraken bijwerken. Wijzigingen krijgen een datum en komen in Overleg.'
					: `Uit de stalafspraken. Beheer: ${shell.admins.join(' en ') || 'de beheerders'}.`
			}
			footer={{
				cancel: canEdit ? 'Sluiten' : null,
				primary: canEdit
					? {
							label: '+ Afspraak toevoegen',
							onClick: () => go({ kind: 'regel', chapter }),
						}
					: { label: 'Sluiten', tone: 'quiet', onClick: onClose },
			}}
		>
			<OyTrayField label="Afspraken">
				{rows.length ? (
					<OyTrayRows
						rows={rows.map((r, i) => ({
							a: `${i + 1}.`,
							b: r.textNl,
							c: r.edited ? `bijgewerkt ${r.changedAt}` : undefined,
						}))}
					/>
				) : (
					<p className="st-status">Nog geen afspraken in dit hoofdstuk.</p>
				)}
			</OyTrayField>
		</OyTray>
	)
}

function RegelTray({
	shell,
	open,
	onClose,
	go,
	chapter: init,
}: TrayProps & { chapter?: RuleChapter }) {
	const chapters = (Object.keys(RULE_CHAPTER_NL) as RuleChapter[]).filter(
		(c) => c !== 'voer',
	)
	const [chapter, setChapter] = useState<RuleChapter>(init ?? 'staldienst')
	const [text, setText] = useState('')
	const { post, busy, error } = useDoorgeven(() => onClose())
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="📜"
			back={
				init
					? {
							label: 'Terug',
							onClick: () => go({ kind: 'regels', chapter: init }),
						}
					: undefined
			}
			title="Afspraak toevoegen"
			intro={`Alleen beheerders (${shell.admins.join(', ') || 'beheer'}). Iedereen ziet de wijziging met datum.`}
			footer={{
				primary: {
					label: busy ? 'Bezig…' : text.trim() ? 'Toevoegen' : 'Schrijf eerst',
					disabled: !text.trim() || busy,
					onClick: () =>
						text.trim() && post({ intent: 'regel-add', chapter, text }),
				},
			}}
		>
			<OyTrayField label="Hoofdstuk">
				{(id) => (
					<OyChipGroup mode="single" labelledBy={id}>
						{chapters.map((c) => (
							<OyChip
								key={c}
								selected={chapter === c}
								onSelect={() => setChapter(c)}
							>
								{RULE_CHAPTER_NL[c].label}
							</OyChip>
						))}
					</OyChipGroup>
				)}
			</OyTrayField>
			<OyTrayField label="Afspraak">
				<OyField
					value={text}
					onChange={(e) => setText(e.target.value)}
					placeholder="bijv. Paddock alleen open tussen 10:00 en 15:00"
					maxLength={400}
				/>
			</OyTrayField>
			<Status error={error} busy={busy} />
		</OyTray>
	)
}

// ── Persoon ─────────────────────────────────────────────────────────────────

function PersoonTray({
	shell,
	open,
	onClose,
	go,
	personId,
}: TrayProps & { personId: string }) {
	const person =
		shell.people.find((p) => p.id === personId) ??
		(personId === shell.e.id ? shell.e : null)
	const read = useRead('persoon')
	useEffect(() => {
		read.load({ persoon: personId })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [personId])
	if (!person) return null
	const roleLine = [
		person.isInstructor
			? 'instructeur'
			: person.isAdmin
				? 'stal, beheer'
				: 'stal',
		...person.rides.map((h) => `rijdt ${h}`),
	].join(', ')
	const dayLine =
		person.fixedWeekday != null
			? `vaste dag: ${WEEKDAYS_NL[person.fixedWeekday]}`
			: null
	const v = read.value
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon={person.firstName.charAt(0)}
			iconBg={person.hue}
			iconFg="#fff"
			title={person.name}
			intro={[roleLine, dayLine].filter(Boolean).join(', ')}
			footer={{
				cancel: null,
				primary: { label: 'Klaar', tone: 'quiet', onClick: onClose },
			}}
		>
			{!person.isInstructor && person.id !== shell.e.id ? (
				<OyTrayField label="Afwezig">
					{(id) => (
						<OyChipGroup mode="single" labelledBy={id}>
							<OyChip
								selected={(v?.absences.length ?? 0) === 0}
								onSelect={() => {}}
							>
								{v && v.absences.length
									? v.absences.map((a) => a.label).join(', ')
									: 'niets gepland'}
							</OyChip>
							<OyChip
								selected={false}
								onSelect={() => go({ kind: 'afwezig', who: person.id })}
							>
								afwezigheid doorgeven…
							</OyChip>
						</OyChipGroup>
					)}
				</OyTrayField>
			) : null}
			<OyTrayField label="Dit jaar">
				<OyTrayRows
					rows={[
						{
							a: 'beurten',
							b: v
								? `${v.beurten}${v.goal != null ? `, afspraak ${v.goal}` : ''}`
								: '…',
						},
						{
							a: 'klus-uren',
							b: v ? `${String(v.hours).replace('.', ',')} u` : '…',
						},
					]}
				/>
			</OyTrayField>
		</OyTray>
	)
}

// ── Foto's ──────────────────────────────────────────────────────────────────

type AtOption = {
	key: string
	label: string
	short: string
	kind: 'les' | 'mestdienst' | 'klus' | 'paard' | 'los'
	id?: string
}

function FotoTray({
	shell,
	open,
	onClose,
	lessonId,
	horse,
}: TrayProps & { lessonId?: string; horse?: string }) {
	const toast = useOyToast()
	const navigate = useNavigate()
	const [picked, setPicked] = useState<PickedPhoto[]>([])
	const [caption, setCaption] = useState('')
	const [busy, setBusy] = useState(0)
	const [error, setError] = useState<string | null>(null)
	const options = useMemo<AtOption[]>(() => {
		const todayLesson = shell.lessons.find(
			(l) => l.dateKey === shell.todayKey && l.riderId === 'E',
		)
		const out: AtOption[] = []
		if (todayLesson)
			out.push({
				key: 'les',
				label: todayLesson.time
					? `Les ${todayLesson.time}, ${shell.e.firstName}`
					: `Les, ${shell.e.firstName}`,
				short: 'Les',
				kind: 'les',
				id: todayLesson.id,
			})
		out.push({
			key: 'mestdienst',
			label: 'Mestdienst',
			short: 'Mestdienst',
			kind: 'mestdienst',
			id: shell.todayKey,
		})
		out.push({
			key: 'klus',
			label: 'Klusje',
			short: 'Klusje',
			kind: 'klus',
			id: shell.todayKey,
		})
		for (const h of shell.horses)
			out.push({
				key: `paard:${h.name}`,
				label: `${h.name}, ${h.statusLabel}`,
				short: h.name,
				kind: 'paard',
				id: h.name,
			})
		out.push({ key: 'los', label: 'Los', short: 'Los', kind: 'los' })
		return out
	}, [shell])
	const [at, setAt] = useState(
		lessonId ? 'les' : horse ? `paard:${horse}` : (options[0]?.key ?? 'los'),
	)
	const chosen =
		options.find((o) => o.key === at) ?? options[options.length - 1]!
	// object URLs live until the tray unmounts (a removed tile revokes its own)
	const pickedRef = useRef(picked)
	pickedRef.current = picked
	useEffect(
		() => () => pickedRef.current.forEach((p) => URL.revokeObjectURL(p.src)),
		[],
	)
	const n = picked.length
	const disabled = !shell.consent.fotos
	async function send() {
		if (!n || busy) return
		setError(null)
		const ids: string[] = []
		for (let i = 0; i < picked.length; i++) {
			setBusy(i + 1)
			const fd = new FormData()
			fd.set('file', picked[i]!.file)
			const res = await fetch('/stal/foto-upload', { method: 'post', body: fd })
			const json = (await res.json().catch(() => null)) as {
				ok: boolean
				id?: string
				error?: string
			} | null
			if (!res.ok || !json?.ok || !json.id) {
				setBusy(0)
				setError(json?.error ?? 'Uploaden lukte niet. Probeer het nog eens.')
				return
			}
			ids.push(json.id)
		}
		const body = new URLSearchParams({
			intent: 'foto-attach',
			photoIds: ids.join(','),
			kind: chosen.kind,
			caption,
		})
		if (chosen.id) body.set('belongsId', chosen.id)
		const res = await fetch(DOORGEVEN, { method: 'post', body })
		const json = (await res.json().catch(() => null)) as DoorgevenResult | null
		setBusy(0)
		if (!json?.ok) {
			setError(json && !json.ok ? json.error : 'Dat lukte niet.')
			return
		}
		toast.say(json.toast)
		setPicked([])
		setCaption('')
		onClose()
		void navigate('/stal/overleg')
	}
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="📷"
			title="Foto's erbij"
			intro={
				disabled
					? "Foto's staan nog niet aan: de kring tekent er eerst voor. Tot dan blijft deze lade dicht."
					: n
						? `${n} gekozen. Waar horen ze bij?`
						: "Kies foto's uit je bibliotheek of maak er een. Ze komen in de groep en bij de dag."
			}
			footer={{
				cancel: null,
				primary: {
					label: busy
						? `Bezig, ${busy} van ${n}…`
						: n
							? `Zet ${n} bij ${chosen.short}`
							: 'Tik je foto’s aan',
					disabled: !n || busy > 0 || disabled,
					onClick: () => void send(),
				},
			}}
			note="Alleen de groep ziet ze. Locatie halen we uit de foto's."
		>
			<OyTrayField label={n ? 'Gekozen' : "Foto's"}>
				<OyPhotoPicker
					picked={picked}
					disabled={disabled || busy > 0}
					onPick={(files) =>
						setPicked((cur) =>
							[
								...cur,
								...files.map((file) => ({
									key: `${file.name}:${file.size}:${file.lastModified}`,
									file,
									src: URL.createObjectURL(file),
								})),
							].filter(
								(p, i, arr) => arr.findIndex((x) => x.key === p.key) === i,
							),
						)
					}
					onRemove={(key) =>
						setPicked((cur) => {
							const gone = cur.find((p) => p.key === key)
							if (gone) URL.revokeObjectURL(gone.src)
							return cur.filter((p) => p.key !== key)
						})
					}
				/>
			</OyTrayField>
			{n ? (
				<OyTrayField label="Hoort bij">
					{(id) => (
						<OyChipGroup mode="single" labelledBy={id}>
							{options.map((o) => (
								<OyChip
									key={o.key}
									selected={at === o.key}
									onSelect={() => setAt(o.key)}
								>
									{o.label}
								</OyChip>
							))}
						</OyChipGroup>
					)}
				</OyTrayField>
			) : null}
			<OyTrayField label="Zeg er iets bij (mag leeg)">
				<OyField
					value={caption}
					onChange={(e) => setCaption(e.target.value)}
					placeholder="bijv. Was gezellig"
					maxLength={280}
					disabled={disabled}
				/>
			</OyTrayField>
			<Status error={error} busy={busy > 0} />
		</OyTray>
	)
}

function FotoViewTray({
	open,
	onClose,
	photoId,
}: TrayProps & { photoId: string }) {
	const read = useRead('foto')
	useEffect(() => {
		read.load({ foto: photoId })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [photoId])
	const p = read.value?.photo ?? null
	return (
		<OyTray
			open={open}
			onClose={onClose}
			icon="🖼"
			title={p?.label ?? 'Foto'}
			intro={p ? `${p.by}, ${p.day}, ${p.ctx}` : undefined}
			footer={{
				cancel: null,
				primary: { label: 'Sluiten', tone: 'quiet', onClick: onClose },
			}}
		>
			<OyPhotoViewer
				src={photoSrc(photoId)}
				alt={p?.label ?? 'foto'}
				meta={
					p
						? [
								p.by,
								p.day,
								p.ctx,
								p.ofE ? 'ook in Elses kring' : 'alleen de groep',
							]
						: []
				}
			/>
		</OyTray>
	)
}
